<template>
  <div class="md-layout photo-manager">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon"><md-icon>photo_library</md-icon></div>
          <h4 class="title">{{ $t("layout.photoManager") }}</h4>
        </md-card-header>
        <md-card-content>
          <!-- Tabs je Bildart (Michael 26.08.): schneller Wechsel, der Drawer bleibt fuer Kombinationen -->
          <div class="ph-tabs">
            <button v-for="t in tabs" :key="t" class="ph-tabs__tab" :class="{ 'is-active': tab === t }" @click="setTab(t)">
              {{ t === "all" ? $t("photos.tabs.all") : $t("photos.sources." + t) }}
              <span class="ph-tabs__count">{{ tabCount(t) }}</span>
            </button>
          </div>
          <!-- Ansicht: Kachel gross (6/Reihe), Kachel klein (8/Reihe), Liste -->
          <div class="ph-toolbar">
            <div class="ph-view">
              <button v-for="v in views" :key="v" class="ph-view__btn" :class="{ 'is-active': view === v }" :title="$t('photos.view.' + v)" @click="setView(v)">
                <md-icon>{{ viewIcon(v) }}</md-icon>
              </button>
            </div>
            <span class="ph-count">{{ $t("photos.count", { shown: items.length, total }) }}</span>
          </div>

          <p v-if="loading && !items.length" class="ph-hint">{{ $t("photos.loading") }}</p>
          <p v-else-if="!items.length" class="ph-hint">{{ $t("photos.empty") }}</p>

          <!-- Kachelansicht -->
          <div v-if="view !== 'list' && items.length" class="ph-grid" :class="'ph-grid--' + view">
            <div v-for="it in items" :key="key(it)" class="ph-tile" :title="tileTitle(it)" @click="openZoom(it)">
              <div class="ph-tile__img">
                <img v-if="thumbs[key(it)]" :src="thumbs[key(it)]" alt="" />
                <md-icon v-else class="ph-tile__placeholder">image</md-icon>
                <span class="ph-tile__badge" :class="'ph-badge--' + it.source">{{ $t("photos.sourcesShort." + it.source) }}</span>
              </div>
              <div class="ph-tile__cap">
                <div class="ph-tile__ref">{{ it.reference }}</div>
                <div class="ph-tile__sub">{{ it.photoType || it.title || "" }}</div>
                <div v-if="it.uploadedOn" class="ph-tile__date">{{ it.uploadedOn | formatDate }}</div>
              </div>
            </div>
          </div>

          <!-- Listenansicht -->
          <table v-if="view === 'list' && items.length" class="ph-list">
            <thead>
              <tr>
                <th></th>
                <th>{{ $t("photos.list.source") }}</th>
                <th>{{ $t("photos.list.reference") }}</th>
                <th>{{ $t("photos.list.title") }}</th>
                <th>{{ $t("photos.list.photoType") }}</th>
                <th>{{ $t("photos.list.date") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="it in items" :key="key(it)" @click="openZoom(it)">
                <td class="ph-list__thumb"><img v-if="thumbs[key(it)]" :src="thumbs[key(it)]" alt="" /></td>
                <td>
                  <span class="ph-tile__badge ph-tile__badge--inline" :class="'ph-badge--' + it.source">{{ $t("photos.sources." + it.source) }}</span>
                </td>
                <td>{{ it.reference }}</td>
                <td>
                  {{ it.title }}<small v-if="it.subtitle"> · {{ it.subtitle }}</small>
                </td>
                <td>{{ it.photoType || "–" }}</td>
                <td>{{ it.uploadedOn ? $options.filters.formatDateTime(it.uploadedOn) : "–" }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="items.length < total" class="ph-more">
            <md-button class="md-simple md-sm" :disabled="loading" @click="loadMore">{{ loading ? $t("photos.loading") : $t("photos.loadMore", { count: total - items.length }) }}</md-button>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <!-- Dock rechts: Suche + Filter (wie Lieferplan/Retouren) -->
    <div class="ph-dock">
      <button class="ph-dock__trigger" :class="{ 'ph-dock__trigger--active': hasSearch }" :title="$t('photos.search.title')" @click.stop="searchOpen = !searchOpen">
        <md-icon>search</md-icon>
      </button>
      <photos-drawer-filter :photo-types="photoTypes" @apply-filters="applyFilters" @opened="onDrawerOpened" @closed="drawerOpen = false" />
    </div>
    <div v-if="searchOpen && !drawerOpen" class="ph-dock__panel">
      <md-field md-clearable>
        <label>{{ $t("photos.search.general") }}</label>
        <md-input v-model="q" @input="debouncedReload"></md-input>
      </md-field>
      <md-field md-clearable>
        <label>{{ $t("photos.search.sku") }}</label>
        <md-input v-model="sku" @input="debouncedReload"></md-input>
      </md-field>
    </div>

    <image-zoom :url="zoomUrl" @close="closeZoom" />
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import PhotosService from "@/services/photos.service";
import ReturnsService from "@/services/returns.service";
import ImageZoom from "@/components/Products/ImageZoom.vue";
import PhotosDrawerFilter from "./PhotosDrawerFilter.vue";

const PAGE_SIZE = 48;
const VIEW_KEY = "occ.photos.view";

export default {
  name: "PhotoManager",
  components: { ImageZoom, PhotosDrawerFilter },
  data() {
    let view = "large";
    try {
      view = localStorage.getItem(VIEW_KEY) || "large";
    } catch (e) {
      view = "large";
    }
    return {
      items: [],
      total: 0,
      page: 0,
      loading: false,
      thumbs: {},
      view,
      views: ["large", "small", "list"],
      tab: "all",
      tabs: ["all", "product", "order", "return"],
      counts: {},
      q: "",
      sku: "",
      filters: { sources: [], photoTypeId: null },
      photoTypes: [],
      searchOpen: false,
      drawerOpen: false,
      zoomUrl: null,
    };
  },
  computed: {
    hasSearch() {
      return !!(this.q || this.sku);
    },
  },
  created() {
    this.debouncedReload = debounce(this.reload, 350);
    ReturnsService.photoTypes()
      .then((t) => (this.photoTypes = (t || []).filter((x) => x.isActive !== false)))
      .catch(() => (this.photoTypes = []));
    this.reload();
  },
  beforeDestroy() {
    Object.values(this.thumbs).forEach((u) => URL.revokeObjectURL(u));
    if (this.zoomUrl) URL.revokeObjectURL(this.zoomUrl);
  },
  methods: {
    key(it) {
      return it.source + ":" + it.id;
    },
    viewIcon(v) {
      return v === "large" ? "grid_view" : v === "small" ? "apps" : "view_list";
    },
    setView(v) {
      this.view = v;
      try {
        localStorage.setItem(VIEW_KEY, v);
      } catch (e) {
        /* Speicherung ist nur Komfort */
      }
    },
    tileTitle(it) {
      return [it.reference, it.title, it.subtitle, it.photoType].filter(Boolean).join(" · ");
    },
    tabCount(t) {
      if (t === "all") return Object.values(this.counts).reduce((a, b) => a + b, 0);
      return this.counts[t] || 0;
    },
    setTab(t) {
      this.tab = t;
      this.filters.sources = t === "all" ? [] : [t];
      this.reload();
    },
    applyFilters(f) {
      this.filters = { sources: f.sources || [], photoTypeId: f.photoTypeId };
      // Tab zum Drawer synchron halten: genau eine Art -> deren Tab, sonst "Alle"
      this.tab = this.filters.sources.length === 1 ? this.filters.sources[0] : "all";
      this.reload();
    },
    onDrawerOpened() {
      this.drawerOpen = true;
      this.searchOpen = false;
    },
    async reload() {
      this.page = 0;
      this.items = [];
      await this.fetch();
    },
    async loadMore() {
      this.page += 1;
      await this.fetch();
    },
    async fetch() {
      this.loading = true;
      try {
        const res = await PhotosService.list({
          sources: this.filters.sources.length ? this.filters.sources.join(",") : undefined,
          photoTypeId: this.filters.photoTypeId || undefined,
          q: this.q || undefined,
          sku: this.sku || undefined,
          pageIndex: this.page,
          pageSize: PAGE_SIZE,
        });
        const data = res.data || [];
        this.items = this.page === 0 ? data : this.items.concat(data);
        this.total = res.total || 0;
        this.counts = res.counts || {};
        this.loadThumbs(data);
      } finally {
        this.loading = false;
      }
    },
    // Thumbnails nacheinander in kleinen Gruppen laden - 48 parallele Requests wuerden die API und
    // den Browser unnoetig belasten; ein einzelnes fehlendes Bild bricht nichts ab.
    async loadThumbs(list) {
      const pending = list.filter((it) => !this.thumbs[this.key(it)]);
      for (let i = 0; i < pending.length; i += 6) {
        await Promise.all(
          pending.slice(i, i + 6).map(async (it) => {
            const url = await PhotosService.contentUrl(it.source, it.id, "thumb").catch(() => null);
            if (url) this.$set(this.thumbs, this.key(it), url);
          })
        );
      }
    },
    async openZoom(it) {
      const url = await PhotosService.contentUrl(it.source, it.id).catch(() => null);
      if (url) this.zoomUrl = url;
    },
    closeZoom() {
      if (this.zoomUrl) URL.revokeObjectURL(this.zoomUrl);
      this.zoomUrl = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.ph-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #eee;
  margin-bottom: 10px;

  &__tab {
    border: none;
    background: transparent;
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;

    &.is-active {
      color: #7f915a;
      border-bottom-color: #7f915a;
    }
  }
  &__count {
    display: inline-block;
    margin-left: 6px;
    font-size: 11px;
    background: #eee;
    color: #555;
    border-radius: 10px;
    padding: 1px 7px;
  }
  .is-active &__count {
    background: #f1f5e8;
    color: #7f915a;
  }
}
.ph-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.ph-view {
  display: inline-flex;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;

  &__btn {
    border: none;
    background: #fff;
    width: 38px;
    height: 32px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    & + & {
      border-left: 1px solid #ddd;
    }
    &.is-active {
      background: #7f915a;

      .md-icon {
        color: #fff !important;
      }
    }
  }
}
.ph-count {
  font-size: 12px;
  color: #666;
}
.ph-hint {
  color: #999;
  text-align: center;
  padding: 30px 0;
}
.ph-grid {
  display: grid;
  gap: 12px;

  &--large {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
  &--small {
    grid-template-columns: repeat(8, minmax(0, 1fr));
    gap: 8px;
  }
  @media (max-width: 1400px) {
    &--large {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
    &--small {
      grid-template-columns: repeat(6, minmax(0, 1fr));
    }
  }
  @media (max-width: 900px) {
    &--large {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    &--small {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
}
.ph-tile {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  cursor: zoom-in;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.16);
  }
  &__img {
    position: relative;
    aspect-ratio: 1 / 1;
    background: #f3f3f3;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }
  &__placeholder {
    color: #bbb !important;
    font-size: 40px !important;
  }
  &__badge {
    position: absolute;
    top: 6px;
    left: 6px;
    font-size: 10px;
    font-weight: 600;
    color: #fff;
    padding: 2px 6px;
    border-radius: 4px;
    letter-spacing: 0.3px;

    &--inline {
      position: static;
      display: inline-block;
    }
  }
  &__cap {
    padding: 6px 8px 7px;
    line-height: 1.25;
  }
  &__ref {
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__sub,
  &__date {
    font-size: 11px;
    color: #777;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.ph-grid--small .ph-tile__cap {
  padding: 4px 6px 5px;
}
.ph-grid--small .ph-tile__sub {
  display: none;
}
.ph-badge--product {
  background: #1e88e5;
}
.ph-badge--order {
  background: #fb8c00;
}
.ph-badge--return {
  background: #7f915a;
}
.ph-list {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    color: #7f915a;
    font-weight: 600;
    padding: 6px 8px;
    border-bottom: 1px solid #eee;
  }
  td {
    padding: 4px 8px;
    border-bottom: 1px solid #f2f2f2;
    vertical-align: middle;
  }
  tbody tr {
    cursor: zoom-in;

    &:hover {
      background: #fafafa;
    }
  }
  &__thumb img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
    display: block;
  }
}
.ph-more {
  text-align: center;
  margin-top: 12px;
}

/* Dock rechts - gleiche Masse wie Lieferplan/Retouren */
.ph-dock {
  position: fixed;
  top: 110px;
  right: 0;
  z-index: 1048;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ph-dock ::v-deep .ph-dock__trigger,
.ph-dock__trigger {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 8px 0 0 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.ph-dock ::v-deep .ph-dock__trigger--active,
.ph-dock__trigger--active {
  background: #7f915a;

  .md-icon {
    color: #fff !important;
  }
}
.ph-dock ::v-deep .ph-dock__badge {
  position: absolute;
  top: 2px;
  right: 4px;
  background: #f44336;
  color: #fff;
  border-radius: 50%;
  min-width: 16px;
  height: 16px;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
}
.ph-dock__panel {
  position: fixed;
  top: 110px;
  right: 52px;
  z-index: 1041;
  width: 280px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.22);
  padding: 8px 16px;
}
</style>
